/**
 * Legal Page Layout
 * Shared layout for all legal pages (Terms, Privacy, Cookies, etc.)
 */

import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Icon,
  useColorModeValue,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { MdHome, MdChevronRight } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import { formatLegalDate, LEGAL_CONFIG } from '../../config/legal';

/**
 * Legal Page Layout Component
 * @param {Object} props
 * @param {string} props.title - Page title
 * @param {string} props.subtitle - Optional subtitle
 * @param {string} props.documentType - Document type for metadata (terms, privacy, cookies, refund, aup)
 * @param {React.ReactNode} props.children - Page content
 * @param {string} props.icon - Optional icon component
 */
const LegalPageLayout = ({
  title,
  subtitle,
  documentType,
  children,
  icon: IconComponent,
}) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'es';

  // Colors - All hooks must be called at the top level
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const iconColor = useColorModeValue('brand.600', 'brand.300');
  const headingColor = useColorModeValue('gray.800', 'white');

  // Get document metadata
  const documentMeta = LEGAL_CONFIG.documents[documentType];
  const lastUpdated = documentMeta?.lastUpdated
    ? formatLegalDate(
        documentMeta.lastUpdated,
        lang === 'en' ? 'en-US' : 'es-ES'
      )
    : null;

  return (
    <Box bg={bgColor} minH="100vh" py={8}>
      <Container maxW="container.lg">
        {/* Breadcrumb */}
        <Breadcrumb
          spacing="8px"
          separator={<Icon as={MdChevronRight} color={mutedColor} />}
          mb={6}
        >
          <BreadcrumbItem>
            <BreadcrumbLink as={RouterLink} to="/" color={mutedColor}>
              <HStack spacing={1}>
                <Icon as={MdHome} />
                <Text>{t('common.home', 'Inicio')}</Text>
              </HStack>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem isCurrentPage>
            <BreadcrumbLink color={textColor} fontWeight="medium">
              {title}
            </BreadcrumbLink>
          </BreadcrumbItem>
        </Breadcrumb>

        {/* Main Card */}
        <Box
          bg={cardBgColor}
          borderRadius="xl"
          borderWidth="1px"
          borderColor={borderColor}
          overflow="hidden"
          boxShadow="sm"
        >
          {/* Header */}
          <Box
            bg={useColorModeValue('brand.50', 'brand.900')}
            borderBottomWidth="1px"
            borderColor={borderColor}
            px={{ base: 6, md: 10 }}
            py={{ base: 6, md: 8 }}
          >
            <VStack align="flex-start" spacing={3}>
              <HStack spacing={3}>
                {IconComponent && (
                  <Icon as={IconComponent} boxSize={8} color={iconColor} />
                )}
                <Heading as="h1" size="xl" color={headingColor}>
                  {title}
                </Heading>
              </HStack>
              {subtitle && (
                <Text fontSize="lg" color={mutedColor}>
                  {subtitle}
                </Text>
              )}
              {lastUpdated && (
                <HStack spacing={3} flexWrap="wrap">
                  <Text fontSize="sm" color={mutedColor}>
                    {t('legal.lastUpdated', 'Ultima actualizacion')}:{' '}
                    {lastUpdated}
                  </Text>
                </HStack>
              )}
            </VStack>
          </Box>

          {/* Content */}
          <Box px={{ base: 6, md: 10 }} py={{ base: 6, md: 8 }}>
            <VStack align="stretch" spacing={8}>
              {children}
            </VStack>
          </Box>

          {/* Footer */}
          <Box
            bg={useColorModeValue('gray.50', 'gray.900')}
            borderTopWidth="1px"
            borderColor={borderColor}
            px={{ base: 6, md: 10 }}
            py={6}
          >
            <VStack spacing={2} align="flex-start">
              <Text fontSize="sm" color={mutedColor}>
                {LEGAL_CONFIG.company.name} - {LEGAL_CONFIG.company.address}
              </Text>
              <Text fontSize="sm" color={mutedColor}>
                {t('legal.questions', 'Preguntas sobre este documento?')}{' '}
                <Text
                  as="a"
                  href={`mailto:${LEGAL_CONFIG.contact.legal}`}
                  color="brand.500"
                  textDecoration="underline"
                >
                  {LEGAL_CONFIG.contact.legal}
                </Text>
              </Text>
            </VStack>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default LegalPageLayout;
